import React from 'react';
import { Download, Users, Building2, Stethoscope, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';

const pitchDecks = [
  {
    id: 'investor',
    title: 'Investor Pitch Deck',
    description: 'For VCs, angel investors, and funding partners. Includes market analysis, financial projections, and funding ask.',
    icon: Users,
    color: 'bg-purple-500',
    filename: 'Afyagram_Investor_Pitch.pptx',
    slides: 12
  },
  {
    id: 'partner',
    title: 'Strategic Partner Pitch Deck',
    description: 'For hospitals, insurance companies, and healthcare institutions. Partnership models and integration options.',
    icon: Building2,
    color: 'bg-blue-500',
    filename: 'Afyagram_Partner_Pitch.pptx',
    slides: 10
  },
  {
    id: 'doctor',
    title: 'Healthcare Provider Pitch Deck',
    description: 'For doctors and medical practitioners. Benefits of joining Afyagram, features, and pricing.',
    icon: Stethoscope,
    color: 'bg-green-500',
    filename: 'Afyagram_Doctor_Pitch.pptx',
    slides: 10
  },
  {
    id: 'general',
    title: 'General Stakeholder Pitch Deck',
    description: 'Comprehensive overview for all audiences. Vision, features, roadmap, and how to get involved.',
    icon: Globe,
    color: 'bg-orange-500',
    filename: 'Afyagram_General_Pitch.pptx',
    slides: 12
  }
];

export default function PitchDecksPage() {
  const handleDownload = (filename) => {
    window.open(`/${filename}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar />

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-manrope font-bold text-4xl md:text-5xl text-[#0F2A44] mb-4">
            Afyagram Pitch Decks
          </h1>
          <p className="font-public text-lg text-[#4B5563] max-w-2xl mx-auto">
            Download our professionally crafted pitch decks tailored for different audiences. 
            All presentations are fully editable in PowerPoint.
          </p>
        </div>

        {/* Pitch Deck Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pitchDecks.map((deck) => {
            const Icon = deck.icon;
            return (
              <div 
                key={deck.id}
                className="bg-white rounded-2xl border border-slate-100 shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                {/* Card Header */}
                <div className={`${deck.color} p-6`}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                      <Icon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h2 className="font-manrope font-bold text-xl text-white">
                        {deck.title}
                      </h2>
                      <p className="text-white/80 text-sm font-public">
                        {deck.slides} slides • PowerPoint format
                      </p>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <p className="font-public text-[#4B5563] mb-6">
                    {deck.description}
                  </p>

                  <Button
                    onClick={() => handleDownload(deck.filename)}
                    className="w-full bg-[#0F2A44] hover:bg-[#0F2A44]/90 text-white rounded-lg h-12 font-manrope font-semibold"
                  >
                    <Download className="h-5 w-5 mr-2" />
                    Download Pitch Deck
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer Note */}
        <div className="mt-12 text-center">
          <div className="bg-[#2DD4BF]/10 rounded-xl p-6 max-w-2xl mx-auto">
            <h3 className="font-manrope font-semibold text-lg text-[#0F2A44] mb-2">
              Need a Custom Presentation?
            </h3>
            <p className="font-public text-[#4B5563] mb-4">
              Contact us for tailored pitch decks or presentations for specific meetings and events.
            </p>
            <a 
              href="mailto:info@afyagram.com"
              className="text-[#2DD4BF] hover:text-[#2DD4BF]/80 font-medium"
            >
              info@afyagram.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
